import React, { useCallback, useEffect, useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/Textarea";

/**
 * The field itself, as a real component rather than the body of a render prop.
 *
 * `useRef` and `useEffect` used to sit inside `Controller`'s `render`. That runs
 * during Controller's own render, so the hooks attached to Controller's fiber and
 * happened to keep their order — but it breaks the rule of hooks, eslint flags
 * it, and it stops being an accident that works the moment anyone makes the
 * render prop return early. A component owns its own hooks.
 *
 * Both refs are set. `field.ref` is how react-hook-form reaches the node, and
 * overwriting it with the autoResize ref left `shouldFocusError` and `setFocus`
 * doing nothing on every textarea in the app, so the first bad field never took
 * focus.
 */
const TextAreaField = ({ field, error, id, rows, placeholder, disabled, maxLength, autoResize, counter, inputClass }) => {
  const nodeRef = useRef(null);

  const setRefs = useCallback(
    (node) => {
      nodeRef.current = node;
      field.ref(node);
    },
    [field],
  );

  useEffect(() => {
    if (!autoResize || !nodeRef.current) return;

    nodeRef.current.style.height = "0px";
    nodeRef.current.style.height = `${nodeRef.current.scrollHeight}px`;
  }, [field.value, autoResize]);

  const length = field.value?.length ?? 0;

  // "0/4000" under an empty box is noise on a field nobody is close to
  // filling, so a caller can ask for it only once the ceiling is in sight.
  // `always` stays the default: every form that had a counter keeps it, and
  // keeps the height it reserves.
  const showCounter = Boolean(maxLength) && (counter === "always" || (counter === "near-limit" && length >= maxLength * 0.9));

  return (
    <>
      <Textarea
        {...field}
        id={id}
        ref={setRefs}
        value={field.value ?? ""}
        rows={rows}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        aria-describedby={error && id ? `${id}-error` : undefined}
        className={cn(
          "w-full rounded-md px-4 py-2.5 text-sm bg-background-secondary text-text-primary border",
          autoResize && "resize-none overflow-hidden",
          error ? "border-status-error" : "border-border-input",
          disabled && "opacity-50 cursor-not-allowed",
          inputClass,
        )}
      />

      {/* Only an `always` counter keeps its line reserved; the other modes
          take one only while there is an error or a counter to show, so an
          empty line does not sit under the box. */}
      {(counter === "always" || error || showCounter) && (
        <div className="flex items-center justify-between mt-1 min-h-[18px]">
          <div className="flex-1">
            {error && (
              <p id={id ? `${id}-error` : undefined} className="text-status-error text-xs">
                {error.message}
              </p>
            )}
          </div>
          {showCounter && <p className={cn("text-xs ml-2 shrink-0", length >= maxLength ? "text-status-error" : "text-text-secondary")}>{`${length}/${maxLength}`}</p>}
        </div>
      )}
    </>
  );
};

/**
 * RHFTextArea Component
 * A reusable textarea component integrated with React Hook Form.
 *
 * @param {string} name - The name of the textarea field (used for form data).
 * @param {string} label - The label to be displayed above the textarea.
 * @param {string} placeholder - The placeholder text for the textarea.
 * @param {number} [rows=5] - The number of rows for the textarea.
 * @param {string} [className=''] - Additional custom classes for the wrapper div.
 * @param {string} [inputClass=''] - Additional custom classes for the textarea.
 * @param {string} [id] - Ties the label and the error message to the field. Pass it.
 * @param {boolean} [hideLabel] - Keeps the label for a screen reader and takes it off screen. The
 *   field still needs one; this is for a composer where the placeholder already says what to write.
 * @param {"always"|"near-limit"|"never"} [counter="always"] - When to show `123/4000`.
 *
 * @returns {JSX.Element} The rendered textarea field with validation.
 */
const RHFTextArea = ({
  name,
  label,
  placeholder,
  rows = 5,
  className = "",
  inputClass = "",
  required = false,
  disabled = false,
  maxLength,
  autoResize = false,
  counter = "always",
  hideLabel = false,
  id,
}) => {
  const { control, formState, getFieldState } = useFormContext();
  const { error } = getFieldState(name, formState);

  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className={cn(hideLabel ? "sr-only" : "block text-sm mb-1.5 text-text-secondary")}>
          {label}
          {required && <span className="text-status-error font-bold text-[16px] ml-1">*</span>}
        </label>
      )}

      <Controller
        name={name}
        control={control}
        defaultValue=""
        render={({ field }) => (
          <TextAreaField
            field={field}
            error={error}
            id={id}
            rows={rows}
            placeholder={placeholder}
            disabled={disabled}
            maxLength={maxLength}
            autoResize={autoResize}
            counter={counter}
            inputClass={inputClass}
          />
        )}
      />
    </div>
  );
};

export default RHFTextArea;
